import { useRef, useState } from 'react';
import { Download, FileImage, FileText, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// Stampply logo - local file to avoid CORS issues
const STAMPPLY_LOGO_URL = "/images/stampply-logo.png";

const QRPosterGenerator = ({ isOpen, onClose, device, merchantLogo, qrCodeData }) => {
  const posterRef = useRef(null);
  const [generating, setGenerating] = useState(false);
  const [slogan, setSlogan] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#1e3a5f');
  const [accentColor, setAccentColor] = useState('#0d9488');

  // Preset color themes
  const colorThemes = [
    { name: 'Clásico', primary: '#1e3a5f', accent: '#0d9488' },
    { name: 'Café', primary: '#78350f', accent: '#92400e' },
    { name: 'Verde', primary: '#14532d', accent: '#15803d' },
    { name: 'Morado', primary: '#581c87', accent: '#7c3aed' },
    { name: 'Rojo', primary: '#7f1d1d', accent: '#dc2626' },
    { name: 'Azul', primary: '#1e40af', accent: '#3b82f6' },
  ];

  const downloadAsPNG = async () => {
    if (!posterRef.current) return;
    setGenerating(true);
    
    try {
      const canvas = await html2canvas(posterRef.current, {
        scale: 3,
        backgroundColor: '#ffffff',
        useCORS: true,
        allowTaint: true,
        logging: false
      });
      
      const link = document.createElement('a');
      link.download = `cartel-qr-${device?.name || 'stampply'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Error generating PNG:', error);
    } finally {
      setGenerating(false);
    }
  };

  const downloadAsPDF = async () => {
    if (!posterRef.current) return;
    setGenerating(true);
    
    try {
      const canvas = await html2canvas(posterRef.current, {
        scale: 3,
        backgroundColor: '#ffffff',
        useCORS: true,
        allowTaint: true,
        logging: false
      });
      
      // A5 dimensions in mm: 148 x 210
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a5'
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdfWidth = 148;
      const pdfHeight = 210;
      
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`cartel-qr-${device?.name || 'stampply'}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setGenerating(false);
    }
  };

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const logoUrl = merchantLogo ? (merchantLogo.startsWith('http') ? merchantLogo : `${BACKEND_URL}${merchantLogo}`) : null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Generar Cartel QR (A5)</DialogTitle>
          <DialogDescription>
            Personaliza y descarga un cartel listo para imprimir
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Preview */}
          <div className="flex-1 flex justify-center">
            <div className="border border-slate-200 rounded-lg shadow-lg overflow-hidden">
              {/* A5 Poster Preview - Aspect ratio 148:210 */}
              <div 
                ref={posterRef}
                className="bg-white flex flex-col"
                style={{ 
                  width: '350px', 
                  height: '495px',
                  padding: '20px'
                }}
              >
                {/* Merchant Logo - Large */}
                <div className="flex justify-center mb-2" style={{ height: '100px' }}>
                  {logoUrl ? (
                    <img 
                      src={logoUrl} 
                      alt="Logo del comercio" 
                      className="h-full w-auto object-contain"
                      crossOrigin="anonymous"
                    />
                  ) : (
                    <div className="h-full flex items-center justify-center text-slate-400 text-sm font-medium border-2 border-dashed border-slate-300 rounded-xl px-6">
                      Tu logo aquí
                    </div>
                  )}
                </div>

                {/* Slogan */}
                {slogan && (
                  <div className="flex justify-center mb-1">
                    <p 
                      className="text-xs font-medium italic text-center"
                      style={{ 
                        color: primaryColor,
                        maxWidth: '140px'
                      }}
                    >
                      "{slogan}"
                    </p>
                  </div>
                )}

                {/* QR Code - Reduced size */}
                <div className="flex justify-center mb-2">
                  {qrCodeData ? (
                    <div 
                      className="p-1.5 rounded-lg"
                      style={{ border: `2px solid ${accentColor}` }}
                    >
                      <img 
                        src={qrCodeData} 
                        alt="Código QR" 
                        className="w-28 h-28 object-contain"
                      />
                    </div>
                  ) : (
                    <div className="w-28 h-28 bg-slate-100 flex items-center justify-center text-slate-400">
                      QR
                    </div>
                  )}
                </div>

                {/* Spanish Text - Smaller */}
                <div className="text-center mb-1 px-1">
                  <p 
                    className="text-xs leading-snug font-medium"
                    style={{ color: primaryColor }}
                  >
                    Escanea el QR y acumula sellos de tu consumición. 
                    Nosotros disfrutamos de tu visita y tú te llevas premios!
                  </p>
                </div>

                {/* English Text - Smaller */}
                <div className="text-center mb-2 px-1">
                  <p 
                    className="leading-snug font-medium italic"
                    style={{ color: accentColor, fontSize: '10px' }}
                  >
                    Scan the QR code and collect stamps with each purchase. 
                    We love having you back — and you get rewards!
                  </p>
                </div>

                {/* Spacer to push footer to bottom */}
                <div className="flex-grow"></div>

                {/* Footer - Stampply Logo left, Location name right */}
                <div className="flex justify-between items-end pt-2 mt-auto px-1">
                  <img 
                    src={STAMPPLY_LOGO_URL}
                    alt="Stampply Rewards" 
                    className="h-16 w-auto object-contain"
                  />
                  {device?.location_name && (
                    <p 
                      className="text-right font-medium truncate max-w-[120px]"
                      style={{ color: primaryColor, fontSize: '9px' }}
                    >
                      {device.location_name}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="lg:w-64 space-y-4">
            <div className="bg-slate-50 rounded-xl p-4">
              <h3 className="font-semibold text-slate-900 mb-2">Descargar cartel</h3>
              <p className="text-sm text-slate-600 mb-4">
                Descarga el cartel en formato A5 listo para imprimir y colocar en tu punto de venta.
              </p>
              
              <div className="space-y-3">
                <Button 
                  onClick={downloadAsPNG}
                  disabled={generating}
                  className="w-full btn-primary"
                >
                  {generating ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <FileImage className="w-5 h-5" />
                      Descargar JPG
                    </>
                  )}
                </Button>
                
                <Button 
                  onClick={downloadAsPDF}
                  disabled={generating}
                  variant="outline"
                  className="w-full"
                >
                  {generating ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <FileText className="w-5 h-5" />
                      Descargar PDF
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Customization Options */}
            <div className="bg-slate-50 rounded-xl p-4">
              <h3 className="font-semibold text-slate-900 mb-3">Personalizar</h3>
              
              {/* Slogan Input */}
              <div className="mb-4">
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Eslogan (opcional)
                </label>
                <input
                  type="text"
                  value={slogan}
                  onChange={(e) => setSlogan(e.target.value)}
                  placeholder="Ej: El mejor café de la ciudad"
                  maxLength={50}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
                <p className="text-xs text-slate-500 mt-1">{slogan.length}/50 caracteres</p>
              </div>

              {/* Color Theme */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  Tema de color
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {colorThemes.map((theme) => (
                    <button
                      key={theme.name}
                      onClick={() => {
                        setPrimaryColor(theme.primary);
                        setAccentColor(theme.accent);
                      }}
                      className={`p-2 rounded-lg border-2 transition-all ${
                        primaryColor === theme.primary 
                          ? 'border-primary ring-2 ring-primary/20' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex gap-1 justify-center mb-1">
                        <div 
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: theme.primary }}
                        />
                        <div 
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: theme.accent }}
                        />
                      </div>
                      <span className="text-xs text-slate-600">{theme.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <h4 className="font-medium text-amber-900 mb-1">💡 Consejo</h4>
              <p className="text-sm text-amber-700">
                Imprime el cartel en papel de calidad y colócalo cerca de la caja o en un lugar visible.
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QRPosterGenerator;
